// src/components/admin/ProductTable.jsx
import React from 'react';
import { Edit2, Trash2, Package, ImageIcon } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';

const ProductTable = ({ products, onEdit, onDelete, onInventory, isReadOnly }) => {
  const { language } = useApp();
  const t = translations[language];

  const getProductName = (product) => {
    switch (language) {
      case 'en': return product.name_en || product.name;
      case 'km': return product.name_km || product.name;
      default: return product.name;
    }
  };

  const getCategoryName = (product) => {
    switch (language) {
      case 'en': return product.category_name_en || product.category_name;
      case 'km': return product.category_name_km || product.category_name;
      default: return product.category_name;
    }
  };

  const getUnitName = (product) => {
    switch (language) {
      case 'en': return product.unit_name_en || product.unit_name || product.unit;
      case 'km': return product.unit_name_km || product.unit_name || product.unit;
      default: return product.unit_name || product.unit;
    }
  };

  const getStockStatus = (product) => {
    const stock = product.stock || 0;
    const minStock = product.min_stock || 0;
    if (stock === 0) return { color: 'bg-red-100 text-red-800', text: language === 'en' ? 'Out of Stock' : '缺货' };
    if (stock <= minStock) return { color: 'bg-orange-100 text-orange-800', text: language === 'en' ? 'Low Stock' : '库存不足' };
    return { color: 'bg-green-100 text-green-800', text: language === 'en' ? 'In Stock' : '有货' };
  };

  if (!products || products.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow text-center py-12">
        <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">{language === 'en' ? 'No products found' : '暂无商品'}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{language === 'en' ? 'Image' : '图片'}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{language === 'en' ? 'Product' : '商品'}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{language === 'en' ? 'Category' : '分类'}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{language === 'en' ? 'Price' : '价格'}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{language === 'en' ? 'Stock' : '库存'}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{language === 'en' ? 'Status' : '状态'}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{language === 'en' ? 'Actions' : '操作'}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {products.map((product) => {
              const stockStatus = getStockStatus(product);
              return (
                <tr key={product.id} className="hover:bg-gray-50">
                  {/* 图片 */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={getProductName(product)}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-gray-400" />
                      )}
                      <div style={{ display: 'none' }} className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-gray-400" />
                      </div>
                    </div>
                  </td>

                  {/* 商品信息 */}
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{getProductName(product)}</div>
                    {product.barcode && <div className="text-xs text-gray-500">{product.barcode}</div>}
                  </td>

                  {/* 分类 */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {getCategoryName(product) || '-'}
                    </span>
                  </td>

                  {/* 价格 */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">${parseFloat(product.sale_price || 0).toFixed(2)}</div>
                    <div className="text-xs text-gray-500">{language === 'en' ? 'Cost' : '成本'}: ${parseFloat(product.cost_price || 0).toFixed(2)}</div>
                  </td>

                  {/* 库存 */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{product.stock || 0} {getUnitName(product)}</div>
                    {product.min_stock > 0 && (
                      <div className="text-xs text-gray-500">{language === 'en' ? 'Min' : '最低'}: {product.min_stock}</div>
                    )}
                  </td>

                  {/* 状态 */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${stockStatus.color}`}>
                      {stockStatus.text}
                    </span>
                  </td>

                  {/* 操作 */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex space-x-2">
                      {/* 编辑：仅总店管理员 */}
                      {onEdit && (
                        <button
                          onClick={() => onEdit(product)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title={t.edit}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}

                      {/* 调整库存：所有人都可以 */}
                      <button
                        onClick={() => onInventory(product)}
                        className="text-green-600 hover:text-green-800 transition-colors"
                        title={language === 'en' ? 'Adjust Stock' : '调整库存'}
                      >
                        <Package className="w-4 h-4" />
                      </button>

                      {/* 删除：仅总店管理员 */}
                      {onDelete && (
                        <button
                          onClick={() => onDelete(product.id)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          title={t.delete}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductTable;